/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useEffect, useRef, useState } from "react";

import { ImageBlock } from "src/classes/block/block-types";
import { Caption } from "src/components/Block/BlockTypes/ImageBlock/lib/Caption";
import { StyledImageBlockContent, StyledImage } from "src/components/Block/BlockTypes/ImageBlock/styles";
import { FileStatusWrapper } from "src/components/Block/BlockTypes/utility/FileStatusWrapper";
import { StyledLoadingMessage } from "src/components/Block/styles";
import { ScribeSpinner } from "src/components/UI/Loading/Spinners/ScribeSpinner/ScribeSpinner";

interface Props {
  block: ImageBlock;
}

export function SImageBlock({ block }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [parentWidth, setParentWidth] = useState<number>(0);

  useEffect(() => {
    if (parentRef.current) {
      setParentWidth(parentRef.current.getBoundingClientRect().width);
    }
  }, [parentRef.current]);

  return (
    <StyledImageBlockContent ref={parentRef}>
      <FileStatusWrapper block={block}>
        <StyledImage
          style={{
            objectFit: "cover",
            display: imgLoading ? "none" : "block",
          }}
          alt="block"
          src={block.getImageLinkForSize(parentWidth > 400 ? "biggest" : "standard")}
          onLoad={() => setImgLoading(false)}
        />
      </FileStatusWrapper>
      {!!block.snapshot.caption && <Caption block={block} editable={false} />}
      {imgLoading && (
        <StyledLoadingMessage>
          <ScribeSpinner />
        </StyledLoadingMessage>
      )}
    </StyledImageBlockContent>
  );
}
